import React from "react";

import { Typography } from "@/components/Typography";
import { Form } from "@/components/Form";

import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { GlobalContext } from "@/contexts/GlobalContext";

import { applyQRFormDefaultValues, onUpdateFormFields, onBindClick } from "@/utils/formHelper";

import { qrWifiValidation } from "@/utils/validators/qr-wifi";

import { FormValidationMethods } from "@/components/Form/types";
import { QRWifiProps } from "./types";

import { useTranslation } from "react-i18next";
import { PAGES } from "@/data/types/pages";
import { NAMESPACES } from "@/internals/constants/namespaces";

import "./styles.scss";

const NO_PASSWORD_VALUE = "No password";

export const QRWifi = ({ data }: QRWifiProps) => {
  const { t } = useTranslation(NAMESPACES.VALIDATIONS);
  const { t: translate } = useTranslation(PAGES.QR_WIFI);
  const { qrData, setQrData } = React.useContext<QREditorContextProps<"wifi">>(QREditorContext);
  const globalContext = React.useContext(GlobalContext);

  const [qrWifiData, setQrWifiData] = React.useState<QRProps<"wifi">>(qrData);
  const [renderForm, setRenderForm] = React.useState(false);
  const [requiredPassword, setRequiredPassword] = React.useState(
    qrData?.data?.qrNetworkSecurityType !== NO_PASSWORD_VALUE
  );

  const dynamicSchema = () => {
    const baseSchema = qrWifiValidation(data, t);
    return baseSchema.map((field) => {
      if (field.name === "qrNetworkPassword") {
        const rules = field.rules as any;
        return {
          ...field,
          rules: {
            ...rules,
            required: {
              value: requiredPassword,
              message: rules.required?.message,
            },
          },
        };
      }
      return field;
    });
  };

  const dataWifiWithDefaultValues = applyQRFormDefaultValues(data, qrData.data);

  onBindClick(dataWifiWithDefaultValues, "qrNetworkSecurityType", (selectedValue: string) => {
    setQrData({
      ...qrData,
      data: {
        ...qrData.data,
        qrNetworkSecurityType: selectedValue,
      },
    });
    if (selectedValue === NO_PASSWORD_VALUE || !selectedValue) {
      disablePassword(globalContext?.context?.formMethods);
    } else {
      enablePassword(globalContext?.context?.formMethods);
    }
  });

  const disablePassword = (methods: FormValidationMethods) => {
    methods?.clearErrors("qrNetworkPassword");
    methods?.resetField("qrNetworkPassword");
    methods?.setValue("qrNetworkPassword", "");

    setRequiredPassword(false);

    setQrData({
      ...qrData,
      data: {
        ...qrData.data,
        qrNetworkSecurityType: NO_PASSWORD_VALUE,
        qrNetworkPassword: "",
      },
    });

    onUpdateFormFields(data.formFields, "qrNetworkPassword", "disabled", true);
  };

  const enablePassword = (methods: FormValidationMethods) => {
    if (!methods) return;

    setRequiredPassword(true);
    onUpdateFormFields(data.formFields, "qrNetworkPassword", "disabled", false);
  };

  const onUpdateState = ({ field, value, component, methods }) => {
    globalContext.setContext({
      ...globalContext.context,
      formMethods: methods,
    });

    setQrData({
      ...qrData,
      data: {
        ...qrData.data,
        [field]: field === "qrNetworkHidden" ? component.checked : value,
      },
    });

    if (field === "qrNetworkSecurityType") {
      value === NO_PASSWORD_VALUE || !value
        ? disablePassword(globalContext?.context?.formMethods)
        : enablePassword(globalContext?.context?.formMethods);
    }
  };

  React.useEffect(() => {
    setQrWifiData(qrData);
  }, [qrData]);

  React.useEffect(() => {
    setQrData({ ...qrWifiData, isValidForm: renderForm });
  }, [renderForm]);

  React.useEffect(() => {
    const passwordLabel = document.querySelector("[for=qrNetworkPassword]");
    if (passwordLabel) {
      // Use textContent instead of innerHTML to avoid DOM manipulation conflicts with React
      passwordLabel.textContent = requiredPassword
        ? translate("network_password_label")
        : translate("network_password_label_not_required");
    }
  }, [requiredPassword]);

  React.useEffect(() => {
    if (
      qrData?.data?.qrNetworkSecurityType === NO_PASSWORD_VALUE ||
      qrData?.data?.qrNetworkSecurityType === undefined
    ) {
      onUpdateState({
        field: "qrNetworkSecurityType",
        value: NO_PASSWORD_VALUE,
        component: undefined,
        methods: globalContext.context.formMethods,
      });
    }
  }, []);

  return (
    <div className="qr-wifi">
      <Typography
        Tag="p"
        size="xl"
        classname="u-bold u-spacing-5"
      >
        {data.title}
      </Typography>
      <Form
        fields={dataWifiWithDefaultValues}
        schema={dynamicSchema()}
        onWatchFields={({ field, value, component, methods }) =>
          onUpdateState({ field, value, component, methods })
        }
        isFormValid={(isValidForm) => setRenderForm(isValidForm)}
      />
    </div>
  );
};
